import type { Driver } from '@cobliteam/api-lib';
import moment from 'moment';
import { z } from 'zod';

const validationSchema = (driver: Driver, allDriversData: Driver[]) => {
  const isValidCPF = (cpf: string) => {
    const cleaned = cpf.replace(/[^\d]/g, '');
    if (cleaned.length !== 11 || /^(\d)\1{10}$/.test(cleaned)) return false;

    const calculateCheckDigit = (factor: number): number => {
      const sum = Array.from(cleaned.slice(0, factor - 1)).reduce((acc, curr, index) => acc + Number.parseInt(curr) * (factor - index), 0);
      const checkDigit = 11 - (sum % 11);
      return checkDigit >= 10 ? 0 : checkDigit;
    };

    return Number.parseInt(cleaned.charAt(9)) === calculateCheckDigit(10) && Number.parseInt(cleaned.charAt(10)) === calculateCheckDigit(11);
  };

  return z.object({
    name: z
      .string()
      .min(1, 'Campo obrigatório')
      .refine((value) => {
        const existentDriverId = 'id' in driver && driver.id;
        const duplicatedDriverNameOnDriverList = allDriversData.find((driverData) => driverData.name === value?.trim());
        return !(
          (duplicatedDriverNameOnDriverList && !existentDriverId) ||
          (existentDriverId && duplicatedDriverNameOnDriverList && duplicatedDriverNameOnDriverList.id !== existentDriverId)
        );
      }, 'Já existe um motorista cadastrado com esse nome'),
    phoneNumbers: z
      .array(z.string())
      .refine((value) => {
        if (value.length === 0) return true;
        if (value[0].length === 0) return true;

        const phoneNumbers = value.map((phone: string) => phone.replace(/[^\d]/g, ''));
        return phoneNumbers.every((phone) => phone.length >= 11);
      }, 'Telefone inválido')
      .transform((value) => value.filter(Boolean).map((phoneNumber) => phoneNumber.replace(/[^\d]/g, '').trim())),
    identificationToken: z
      .object({
        tokenAlias: z
          .string()
          .optional()
          .refine((value) => {
            if (!value) return true;
            const identifier = value.replace(/0-9A-Z/g, '');
            return identifier.length >= 4;
          }, 'Identificador inválido'),
        tokenId: z.string().optional(),
        driverId: z.string().optional(),
        isExternal: z.boolean().optional(),
      })
      .optional(),
    firstLicenseAcquirementDateInMillis: z
      .number()
      .optional()
      .refine((value) => {
        if (!value) return true;
        return moment(value).isSameOrBefore(moment().startOf('day'));
      }, 'Este campo não pode ser posterior à data atual'),
    licenseExpirationDateInMillis: z.number().optional(),
    cpf: z
      .string()
      .optional()
      .refine((value) => {
        if (!value) return true;
        const existentDriverCpf = 'cpf' in driver && driver.cpf;
        const duplicatedDriver = allDriversData.find((d) => d.cpf === value?.trim().replaceAll('.', '').replace('-', ''));
        return !((duplicatedDriver && !existentDriverCpf) || (existentDriverCpf && duplicatedDriver && duplicatedDriver.cpf !== existentDriverCpf));
      }, 'Este CPF já está cadastrado na sua frota. Corrija para continuar.')
      .refine((value) => !value || isValidCPF(value), 'CPF inválido. Corrija para continuar.')
      .transform((value) => {
        const cleanedValue = value?.replace(/[^\d]/g, '').trim();
        return cleanedValue === '' ? null : cleanedValue;
      }),
    driverLicense: z
      .string()
      .optional()
      .refine((value) => {
        if (!value) return true;
        const cleanedCNH = value.replace(/\D/g, '');
        const existentDriverLicense = 'driverLicense' in driver && driver.driverLicense;
        const duplicatedDriver = allDriversData.find((d) => d.driverLicense === cleanedCNH?.trim());
        return !((duplicatedDriver && !existentDriverLicense) || (existentDriverLicense && duplicatedDriver && duplicatedDriver.driverLicense !== existentDriverLicense));
      }, 'Esta CNH já está cadastrada na sua frota. Corrija para continuar.')
      .refine((value) => {
        if (!value) return true;
        const cleanedCNH = value.replace(/\D/g, '');
        return cleanedCNH.length === 11 && cleanedCNH.charAt(0).repeat(11) !== cleanedCNH && cleanedCNH !== '12345678910';
      }, 'CNH inválida. Corrija para continuar.')
      .transform((value) => {
        const cleanedValue = value?.replace(/[^\d]/g, '').trim();
        return cleanedValue === '' ? null : cleanedValue;
      }),
    registryId: z
      .string()
      .nullish()
      .refine((value) => !value?.includes(' '), 'A matrícula não pode conter espaços em branco')
      .refine((value) => {
        const existentDriverRegistryId = 'registryId' in driver && driver.registryId;
        const duplicatedDriver = allDriversData.find((d) => d.registryId === value?.trim());

        return !((duplicatedDriver && !existentDriverRegistryId) || (existentDriverRegistryId && duplicatedDriver && duplicatedDriver.registryId !== existentDriverRegistryId));
      }, 'Este número de matrícula já está cadastrado na sua frota. Corrija para continuar.'),
    licenseCategory: z.string().optional(),
  });
};

export type FormValues = z.infer<ReturnType<typeof validationSchema>>;

export default validationSchema;
